import * as React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { debounce, useForkRef } from '@material-ui/core/utils'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Media from '@oakwood/oui/Media'
import MediaReveal from '@oakwood/oui/MediaReveal'
import Close from 'components/icons/Close'
import ProductDetails from 'containers/ProductDetails'
import Typography from 'components/Typography'

const HEADER_LABELS_RESERVED_HEIGHT = 440
const IMAGE_MAXIMUM_SCALE = 1.8

export const useStyles = (props) =>
  makeStyles((theme) => ({
    root: {
      '--header-gutter': `${theme.spacing(2)}px`,
      '--header-size': '72px',
      '--main-gutter': `${theme.spacing(2)}px`,
      [theme.breakpoints.up(props.BREAKPOINT_KEY_UP)]: {
        '--header-gutter': `${theme.spacing(4)}px`,
        '--header-size': 'var(--slide-collapsed-size-desktop)',
        display: 'flex',
      },
    },
    header: {
      flexShrink: 0,
      position: 'relative',
      height: 'var(--header-size)',
      transition: theme.transitions.create(['transform'], {
        duration: theme.transitions.duration.complex,
      }),
      [theme.breakpoints.up(props.BREAKPOINT_KEY_UP)]: {
        width: 'var(--header-size)',
        height: 'auto',
        '$active &': {
          transform: 'translate3d(calc(var(--main-gutter) / 2), 0, 0)',
        },
      },
    },
    headerLabelContainer: {
      position: 'absolute',
      width: 'var(--header-size)',
      height: 'var(--header-size)',
      '&:nth-child(1)': {
        left: 0,
        top: 0,
      },
      '&:nth-child(2)': {
        right: 0,
        bottom: 0,
      },
      [theme.breakpoints.up(props.BREAKPOINT_KEY_UP)]: {
        transform: 'rotate(90deg)',
      },
    },
    headerLabel: {
      ...theme.typography.button,
      position: 'absolute',
      top: '50%',
      left: 'inherit',
      right: 'inherit',
      margin: '0 var(--header-gutter)',
      transform: 'translateY(-50%)',
      letterSpacing: '0.065em',
      lineHeight: 1,
      whiteSpace: 'nowrap',
      '$headerLabelContainer:last-child &': {
        color: theme.palette.text.hint,
      },
    },
    main: {
      position: 'relative',
      display: 'flex',
      flexGrow: 1,
      padding: theme.spacing(0, 'var(--main-gutter)', 6),
      [theme.breakpoints.up(props.BREAKPOINT_KEY_UP)]: {
        padding: theme.spacing(0, 'var(--main-gutter)', 8, 'var(--main-gutter)'),
        paddingRight: 'calc(var(--header-size) - 10px)',
      },
    },
    mainProductShowcaseInactive: {
      [theme.breakpoints.up(props.BREAKPOINT_KEY_UP)]: {
        padding: theme.spacing(8, 'var(--main-gutter)'),
      },
    },
    layout: {
      display: 'flex',
      flexDirection: 'column',
      gridGap: theme.spacing(4),
      flexGrow: 1,
      [theme.breakpoints.up(props.BREAKPOINT_KEY_MD)]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
      },
      '& > * ': {
        height: 'fit-content',
      },
    },
    mediaArea: {
      [theme.breakpoints.up(props.BREAKPOINT_KEY_MD)]: {
        gridColumn: 'span 2',
      },
    },
    contentArea: {
      transition: theme.transitions.create(['transform', 'opacity'], {
        duration: theme.transitions.duration.standard,
      }),
      '$inactive &': {
        transform: 'translate3d(0, 40px, 0)',
        transitionDelay: '0ms',
        opacity: 0,
      },
    },
    detailsArea: {
      transitionDelay: '175ms',
      [theme.breakpoints.up(props.BREAKPOINT_KEY_MD)]: {
        gridColumn: 'span 2',
      },
    },
    mediaContainer: {
      '--aspect-ratio': 555 / 324,
      transformOrigin: 'left center',
      width: '100%',
      height: '100%',
      '& > picture': {
        objectFit: 'contain',
      },
      [theme.breakpoints.up(props.BREAKPOINT_KEY_MD)]: {
        '--aspect-ratio': 1,
      },
      [theme.breakpoints.up(props.BREAKPOINT_KEY_MD)]: {
        '--aspect-ratio': 555 / 324,
        height: '324px',
        width: '324px',
      },
      [theme.breakpoints.down(props.BREAKPOINT_KEY_DOWN)]: {
        '--aspect-ratio': 555 / 324,
        transform: 'none !important', // Use `!important` instead of a `useMediaQuery` solution.
      },
      '&.initialized': {
        transition: theme.transitions.create(['transform'], {
          duration: theme.transitions.duration.complex,
        }),
      },
    },
    media: {},
    contentContainer: {
      width: '100%',
      [theme.breakpoints.up(props.BREAKPOINT_KEY_MD)]: {
        maxWidth: 'clamp(335px, 34vw, 400px)',
      },
    },
    cta: {
      marginTop: '24px',
    },
    active: {},
    inactive: {},
    description: {
      padding: theme.spacing(5, 0),
    },
    closeIcon: {
      display: 'none',
      justifyContent: 'flex-end',
      alignItems: 'center',
      height: 'fit-content',
      width: '100%',
      paddingTop: theme.spacing(4),
      '& > *': {
        cursor: 'pointer',
      },
      [theme.breakpoints.up(props.BREAKPOINT_KEY_UP)]: {
        display: 'flex',
      },
      [theme.breakpoints.up(props.BREAKPOINT_KEY_MD)]: {
        gridColumn: 'span 2',
      },
    },
    firstSlideCloseIcon: {
      visibility: 'hidden',
    },
    headerInactive: {
      visibility: 'hidden',
    },
    collapsedCloseIcon: {
      display: 'none',
    },
  }))

const Ingredient = React.forwardRef(function Ingredient(props, ref) {
  const {
    active,
    className,
    mediaProps,
    ingredient,
    handleSlideClick,
    index,
    type = 'ingredient',
    breakpoints = {
      BREAKPOINT_KEY_DOWN: 'xs',
      BREAKPOINT_KEY_UP: 'sm',
      BREAKPOINT_KEY_MD: 'md',
    },
    ...other
  } = props

  const classes = useStyles(breakpoints)()

  const rootRef = React.useRef(null)
  const handleRef = useForkRef(rootRef, ref)
  const mainRef = React.useRef(null)
  const layoutRef = React.useRef(null)
  const mediaContainerRef = React.useRef(null)
  const mediaRef = React.useRef(null)

  const [mainHeight, setMainHeight] = React.useState(0)
  const [layoutHeight, setLayoutHeight] = React.useState(0)
  const [mediaHeight, setMediaHeight] = React.useState(0)

  const syncHeights = React.useCallback(() => {
    setMainHeight(mainRef.current?.clientHeight)
    setLayoutHeight(layoutRef.current?.clientHeight)
    setMediaHeight(mediaRef.current?.clientHeight)
  }, [])

  const handleLoaded = React.useCallback(() => {
    syncHeights()
  }, [syncHeights])

  const handleFaded = React.useCallback(() => {
    mediaContainerRef.current.classList.add('initialized')
  }, [])

  React.useEffect(() => {
    const handleResize = debounce(() => {
      syncHeights()
    })

    window.addEventListener('resize', handleResize, { passive: true })
    return () => {
      handleResize.clear()
      window.removeEventListener('resize', handleResize)
    }
  }, [syncHeights])

  React.useEffect(() => {
    const x = !active ? 'calc((var(--header-size) + var(--main-gutter)) * -1)' : 0
    let y = 0
    let scale = 1

    if (!active && mediaHeight) {
      y = `${(layoutHeight - mediaHeight) / 2}px`

      const missingHeight = mainHeight - mediaHeight
      const targetHeight = mediaHeight + missingHeight - HEADER_LABELS_RESERVED_HEIGHT
      scale = Math.min(targetHeight / mediaHeight, IMAGE_MAXIMUM_SCALE)
    }

    mediaContainerRef.current.style.transform = `translate3d(${x}, ${y}, 0) scale(${scale})`
  }, [active, mainHeight, layoutHeight, mediaHeight])

  return (
    <div
      className={clsx(
        classes.root,
        {
          [classes.active]: active,
          [classes.inactive]: !active,
        },
        className,
      )}
      ref={handleRef}
      {...other}
    >
      <div className={classes.header}>
        <div className={classes.headerLabelContainer}>
          <span className={classes.headerLabel}>{ingredient.name}</span>
        </div>

        <div className={clsx(classes.headerLabelContainer, classes.closeIconContainer)}>
          <span
            className={clsx(classes.headerLabel, {
              [classes.headerInactive]: active,
            })}
          >
            {ingredient?.subtitle || ''}
          </span>
        </div>
      </div>
      <div
        className={clsx(classes.main, {
          [classes.mainProductShowcaseInactive]: !active,
        })}
        ref={mainRef}
      >
        <div className={classes.layout} ref={layoutRef}>
          <div
            className={clsx(classes.closeIcon, {
              [classes.firstSlideCloseIcon]: index === 0,
              [classes.collapsedCloseIcon]: !active,
            })}
          >
            <Close onClick={(e) => handleSlideClick(e, index - 1)} value={index} />
          </div>

          <div className={clsx(classes.mediaArea)}>
            <MediaReveal
              className={clsx(classes.mediaContainer)}
              onLoaded={handleLoaded}
              ref={mediaContainerRef}
              ratio
            >
              <Media
                className={classes.media}
                {...mediaProps}
                onTransitionEnd={handleFaded}
                ref={mediaRef}
              />
            </MediaReveal>
          </div>

          <div className={clsx(classes.contentArea, classes.detailsArea)}>
            <div className={classes.contentContainer}>
              <ProductDetails product={ingredient} hideDescription type={type} />

              {ingredient?.description && (
                <Typography className={classes.description} paragraph>
                  {ingredient?.description}
                </Typography>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})

Ingredient.propTypes = {
  active: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.string,
  mediaProps: PropTypes.object,
  ingredient: PropTypes.object,
  handleSlideClick: PropTypes.func,
  breakpoints: PropTypes.object,
  index: PropTypes.number,
}

export default Ingredient
